import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_SERVICE } from "../Services/CommonApi";
import { commonPayloadFields } from "../helper/storage";
import {
  DELETE_NOTIFICATION_EVENT_ROUTING,
  GET_NOTIFICATION_EVENT_ROUTING_LIST,
  GET_NOTIFICATION_EVENT_TYPES,
  SAVE_NOTIFICATION_EVENT_ROUTING,
} from "../constant/CommonApiUrl";

// Tenant-admin: extra recipients per notification event type. Gated by the
// same temporary admin proxy Feature Toggles uses, pending a real permission.

// Fields arrive snake_case (this model, unlike others, is written that way in
// C#). No messageCode catalog is documented yet, so this just passes the
// server's message through.
export const getEventRoutingMessage = (messageCode, fallbackMessage) =>
  fallbackMessage || "Something went wrong.";

const FETCH_EVENT_TYPES = "notificationEventRouting/fetchEventTypes";
const FETCH_LIST = "notificationEventRouting/fetchList";
const SAVE_ROUTE = "notificationEventRouting/save";
const REMOVE_ROUTE = "notificationEventRouting/remove";

export const fetchNotificationEventTypes = createAsyncThunk(
  FETCH_EVENT_TYPES,
  async (_arg, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_NOTIFICATION_EVENT_TYPES);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const fetchNotificationEventRouting = createAsyncThunk(
  FETCH_LIST,
  async ({ event_type_code } = {}, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(
        GET_NOTIFICATION_EVENT_ROUTING_LIST,
        event_type_code ? { event_type_code } : undefined,
      );
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const saveNotificationEventRouting = createAsyncThunk(
  SAVE_ROUTE,
  async (payload, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(SAVE_NOTIFICATION_EVENT_ROUTING, {
        id: payload.id || 0,
        ...payload,
        updatedby: commonPayloadFields.updatedby,
      });
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const removeNotificationEventRouting = createAsyncThunk(
  REMOVE_ROUTE,
  async ({ id }, thunkAPI) => {
    try {
      // Delete is POST .../Delete?id= — a query param, so it's appended to
      // the URL rather than passed as API_SERVICE.post's body.
      const response = await API_SERVICE.post(
        `${DELETE_NOTIFICATION_EVENT_ROUTING}?id=${id}`,
        {},
      );
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

// EventTypes' item shape isn't documented — normalized defensively so a
// string array or a few plausible object shapes all render.
const normalizeEventType = (item) => {
  if (typeof item === "string") return { code: item, label: item };

  const code =
    item?.event_type_code ?? item?.eventTypeCode ?? item?.code ?? item?.value ?? "";
  const label =
    item?.display_name ??
    item?.displayName ??
    item?.event_type_name ??
    item?.name ??
    item?.label ??
    code;

  return { code, label };
};

const normalizeRecipientRow = (row = {}) => ({
  id: row.id,
  event_type_code: row.event_type_code ?? "",
  recipient_type: row.recipient_type ?? "",
  recipient_id: row.recipient_id ?? null,
  recipient_name: row.recipient_name ?? "",
  isdisabled: row.isdisabled ?? "N",
});

const initialState = {
  eventTypes: [],
  eventTypesStatus: "idle", // idle | loading | loaded | error

  rows: [],
  rowsStatus: "idle", // idle | loading | loaded | error

  lastError: null, // { messageCode, message } from the most recent save/delete
};

const notificationEventRoutingSlice = createSlice({
  name: "notificationEventRouting",
  initialState,
  reducers: {
    clearNotificationEventRoutingError: (state) => {
      state.lastError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationEventTypes.pending, (state) => {
        state.eventTypesStatus = "loading";
      })
      .addCase(fetchNotificationEventTypes.fulfilled, (state, action) => {
        const data = action.payload?.data;
        state.eventTypes = (Array.isArray(data) ? data : []).map(
          normalizeEventType,
        );
        state.eventTypesStatus = "loaded";
      })
      .addCase(fetchNotificationEventTypes.rejected, (state) => {
        state.eventTypesStatus = "error";
      })

      .addCase(fetchNotificationEventRouting.pending, (state) => {
        state.rowsStatus = "loading";
      })
      .addCase(fetchNotificationEventRouting.fulfilled, (state, action) => {
        const data = action.payload?.data;
        state.rows = (Array.isArray(data) ? data : []).map(normalizeRecipientRow);
        state.rowsStatus = "loaded";
      })
      .addCase(fetchNotificationEventRouting.rejected, (state) => {
        state.rowsStatus = "error";
      })

      .addCase(saveNotificationEventRouting.fulfilled, (state, action) => {
        state.lastError = action.payload?.status
          ? null
          : {
              messageCode: action.payload?.messageCode,
              message: action.payload?.message,
            };
      })

      .addCase(removeNotificationEventRouting.fulfilled, (state, action) => {
        state.lastError = action.payload?.status
          ? null
          : {
              messageCode: action.payload?.messageCode,
              message: action.payload?.message,
            };
      });
  },
});

export const { clearNotificationEventRoutingError } =
  notificationEventRoutingSlice.actions;
export default notificationEventRoutingSlice.reducer;
